import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Mic,
  ArrowLeft,
  Maximize2,
  Search,
  Trophy,
  Flag,
  Star,
  TrendingUp,
  Calendar,
  MapPin,
  Users,
  ChevronLeft,
  ChevronRight,
  Clock,
  Award,
  Target,
  User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useSeries } from '../../App';

// Mock driver data with stats
const mockDrivers = [
  {
    id: 1,
    car_number: '1A',
    name: 'Brad Sweet',
    hometown: 'Grass Valley, CA',
    team: 'Kasey Kahne Racing',
    crew_chief: 'Eric Prutzman',
    car_owner: 'Kasey Kahne',
    chassis: 'Maxim',
    engine: 'Ford',
    headshot_url: null,
    stats: {
      wins: 5,
      top5: 18,
      top10: 24,
      starts: 28,
      avg_finish: 6.2,
      avg_start: 4.8,
      points_position: 1,
      points: 3245,
      laps_led: 312,
      quick_times: 8,
    },
    recent_results: [
      { event: 'Knoxville', finish: 1 },
      { event: 'Eldora', finish: 3 },
      { event: 'Williams Grove', finish: 2 },
      { event: 'Lincoln', finish: 5 },
      { event: 'Port Royal', finish: 1 },
    ],
    career: {
      wins: 89,
      championships: 3,
      years_active: 15,
    },
    notes: '2019, 2021, 2023 Series Champion. Has won at 42 different tracks.',
  },
  {
    id: 2,
    car_number: '49',
    name: 'Donny Schatz',
    hometown: 'Fargo, ND',
    team: 'Tony Stewart Racing',
    crew_chief: 'Rick Warner',
    car_owner: 'Tony Stewart',
    chassis: 'J&J',
    engine: 'Chevy',
    headshot_url: null,
    stats: {
      wins: 3,
      top5: 15,
      top10: 22,
      starts: 28,
      avg_finish: 7.1,
      avg_start: 5.2,
      points_position: 2,
      points: 3180,
      laps_led: 198,
      quick_times: 5,
    },
    recent_results: [
      { event: 'Knoxville', finish: 2 },
      { event: 'Eldora', finish: 1 },
      { event: 'Williams Grove', finish: 4 },
      { event: 'Lincoln', finish: 3 },
      { event: 'Port Royal', finish: 6 },
    ],
    career: {
      wins: 312,
      championships: 10,
      years_active: 27,
    },
    notes: '10-time Series Champion. Most wins in series history.',
  },
  {
    id: 3,
    car_number: '2',
    name: 'David Gravel',
    hometown: 'Watertown, CT',
    team: 'Big Game Motorsports',
    crew_chief: 'Cody Jacobs',
    car_owner: 'Tod Quiring',
    chassis: 'Maxim',
    engine: 'Ford',
    headshot_url: null,
    stats: {
      wins: 4,
      top5: 16,
      top10: 21,
      starts: 28,
      avg_finish: 7.5,
      avg_start: 6.1,
      points_position: 3,
      points: 3095,
      laps_led: 245,
      quick_times: 6,
    },
    recent_results: [
      { event: 'Knoxville', finish: 4 },
      { event: 'Eldora', finish: 2 },
      { event: 'Williams Grove', finish: 1 },
      { event: 'Lincoln', finish: 2 },
      { event: 'Port Royal', finish: 3 },
    ],
    career: {
      wins: 78,
      championships: 0,
      years_active: 12,
    },
    notes: '2023 Knoxville Nationals Champion. Connecticut native racing full-time.',
  },
  {
    id: 4,
    car_number: '17',
    name: 'Sheldon Haudenschild',
    hometown: 'Wooster, OH',
    team: 'Stenhouse Jr-Marshall Racing',
    crew_chief: 'Kyle Ripper',
    car_owner: 'Ricky Stenhouse Jr.',
    chassis: 'Rocket',
    engine: 'Chevy',
    headshot_url: null,
    stats: {
      wins: 6,
      top5: 14,
      top10: 19,
      starts: 28,
      avg_finish: 8.2,
      avg_start: 7.4,
      points_position: 4,
      points: 2980,
      laps_led: 289,
      quick_times: 4,
    },
    recent_results: [
      { event: 'Knoxville', finish: 3 },
      { event: 'Eldora', finish: 5 },
      { event: 'Williams Grove', finish: 3 },
      { event: 'Lincoln', finish: 1 },
      { event: 'Port Royal', finish: 8 },
    ],
    career: {
      wins: 45,
      championships: 0,
      years_active: 8,
    },
    notes: 'Son of legendary driver Jac Haudenschild. Known for spectacular driving style.',
  },
  {
    id: 5,
    car_number: '41',
    name: 'Carson Macedo',
    hometown: 'Lemoore, CA',
    team: 'Jason Johnson Racing',
    crew_chief: 'Philip Dietz',
    car_owner: 'Bobbi Johnson',
    chassis: 'Maxim',
    engine: 'Ford',
    headshot_url: null,
    stats: {
      wins: 2,
      top5: 12,
      top10: 18,
      starts: 28,
      avg_finish: 9.1,
      avg_start: 8.2,
      points_position: 5,
      points: 2875,
      laps_led: 156,
      quick_times: 3,
    },
    recent_results: [
      { event: 'Knoxville', finish: 6 },
      { event: 'Eldora', finish: 4 },
      { event: 'Williams Grove', finish: 5 },
      { event: 'Lincoln', finish: 8 },
      { event: 'Port Royal', finish: 2 },
    ],
    career: {
      wins: 28,
      championships: 0,
      years_active: 6,
    },
    notes: 'California native. Won 2021 Knoxville Nationals.',
  },
];

// Stat Card Component
function StatCard({ icon: Icon, label, value, subvalue, color = 'text-slate-600' }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
      {subvalue && <div className="text-xs text-slate-500">{subvalue}</div>}
    </div>
  );
}

// Recent Results Component
function RecentResults({ results }) {
  return (
    <div className="flex items-center gap-2">
      {results.map((result, idx) => (
        <div
          key={idx}
          className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
            result.finish === 1 ? 'bg-amber-100 text-amber-700 border-2 border-amber-300' :
            result.finish <= 3 ? 'bg-emerald-100 text-emerald-700' :
            result.finish <= 5 ? 'bg-blue-100 text-blue-700' :
            result.finish <= 10 ? 'bg-slate-100 text-slate-700' :
            'bg-slate-50 text-slate-400'
          }`}
          title={result.event}
        >
          {result.finish}
        </div>
      ))}
    </div>
  );
}

// Driver Card Component (Large Display)
function DriverCardLarge({ driver }) {
  return (
    <Card className="overflow-hidden">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-6 text-white">
        <div className="flex items-start gap-6">
          {/* Car Number */}
          <div className="w-24 h-24 bg-white rounded-xl flex items-center justify-center border-4 border-slate-600">
            <span className="font-mono font-black text-4xl text-slate-800">{driver.car_number}</span>
          </div>

          {/* Driver Info */}
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-1">{driver.name}</h2>
            <div className="flex items-center gap-4 text-slate-300">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {driver.hometown}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {driver.team}
              </span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
              <span>Crew Chief: {driver.crew_chief}</span>
              <span>•</span>
              <span>Chassis: {driver.chassis}</span>
              <span>•</span>
              <span>Engine: {driver.engine}</span>
            </div>
          </div>

          {/* Points Position */}
          <div className="text-right">
            <div className="text-sm text-slate-400">Points Standing</div>
            <div className="text-4xl font-bold">
              {driver.stats.points_position}<span className="text-lg text-slate-400">th</span>
            </div>
            <div className="text-sm text-slate-400">{driver.stats.points.toLocaleString()} pts</div>
          </div>
        </div>
      </div>

      <CardContent className="p-6">
        {/* Season Stats */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            2026 Season Statistics
          </h3>
          <div className="grid grid-cols-5 gap-3">
            <StatCard icon={Trophy} label="Wins" value={driver.stats.wins} color="text-amber-600" />
            <StatCard icon={Award} label="Top 5" value={driver.stats.top5} color="text-emerald-600" />
            <StatCard icon={Target} label="Top 10" value={driver.stats.top10} color="text-blue-600" />
            <StatCard icon={TrendingUp} label="Avg Finish" value={driver.stats.avg_finish.toFixed(1)} />
            <StatCard icon={Flag} label="Laps Led" value={driver.stats.laps_led} color="text-purple-600" />
          </div>
        </div>

        {/* Recent Results */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Recent Results (Last 5 Events)
          </h3>
          <div className="flex items-center gap-3">
            {driver.recent_results.map((result, idx) => (
              <div key={idx} className="text-center">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg mb-1 ${
                    result.finish === 1 ? 'bg-amber-100 text-amber-700 border-2 border-amber-300' :
                    result.finish <= 3 ? 'bg-emerald-100 text-emerald-700' :
                    result.finish <= 5 ? 'bg-blue-100 text-blue-700' :
                    result.finish <= 10 ? 'bg-slate-100 text-slate-700' :
                    'bg-slate-50 text-slate-400'
                  }`}
                >
                  {result.finish}
                </div>
                <div className="text-xs text-slate-500 truncate w-16">{result.event}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Career Stats */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
              <Star className="w-4 h-4" />
              Career Highlights
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 text-center">
                <div className="text-2xl font-bold text-amber-700">{driver.career.wins}</div>
                <div className="text-xs text-amber-600">Career Wins</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 text-center">
                <div className="text-2xl font-bold text-emerald-700">{driver.career.championships}</div>
                <div className="text-xs text-emerald-600">Championships</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 text-center">
                <div className="text-2xl font-bold text-blue-700">{driver.career.years_active}</div>
                <div className="text-xs text-blue-600">Years Active</div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
              <Mic className="w-4 h-4" />
              Announcer Notes
            </h3>
            <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 border border-slate-200">
              {driver.notes}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Driver Quick Card (Sidebar)
function DriverQuickCard({ driver, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        isSelected
          ? 'border-rms-accent bg-rms-accent/5 ring-2 ring-rms-accent/20'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-10 bg-slate-800 rounded flex items-center justify-center">
          <span className="font-mono font-bold text-white">{driver.car_number}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800 truncate">{driver.name}</div>
          <div className="text-xs text-slate-500 truncate">{driver.team}</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-slate-700">P{driver.stats.points_position}</div>
          <div className="text-xs text-slate-400">{driver.stats.wins}W</div>
        </div>
      </div>
    </button>
  );
}

export default function AnnouncerScreen() {
  useParams();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;

  const [selectedDriver, setSelectedDriver] = useState(mockDrivers[0]);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter drivers by search
  const filteredDrivers = mockDrivers.filter(driver =>
    driver.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    driver.car_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    driver.team.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Navigate through drivers
  const goToPrevDriver = () => {
    const currentIndex = mockDrivers.findIndex(d => d.id === selectedDriver.id);
    const newIndex = currentIndex > 0 ? currentIndex - 1 : mockDrivers.length - 1;
    setSelectedDriver(mockDrivers[newIndex]);
  };

  const goToNextDriver = () => {
    const currentIndex = mockDrivers.findIndex(d => d.id === selectedDriver.id);
    const newIndex = currentIndex < mockDrivers.length - 1 ? currentIndex + 1 : 0;
    setSelectedDriver(mockDrivers[newIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevDriver();
      } else if (e.key === 'ArrowRight') {
        goToNextDriver();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedDriver]);

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Link
            to={`/${seriesSlug}/tools`}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <Mic className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="page-title">Announcer Screen</h1>
            <p className="page-subtitle">Driver information and statistics</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Navigation Buttons */}
          <div className="flex items-center gap-1 border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={goToPrevDriver}
              className="p-2 hover:bg-slate-50 transition-colors"
              title="Previous Driver (Left Arrow)"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="px-3 py-1 bg-slate-50 text-sm font-medium text-slate-600">
              {mockDrivers.findIndex(d => d.id === selectedDriver.id) + 1} / {mockDrivers.length}
            </div>
            <button
              onClick={goToNextDriver}
              className="p-2 hover:bg-slate-50 transition-colors"
              title="Next Driver (Right Arrow)"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Fullscreen */}
          <Button variant="secondary" onClick={handleFullscreen}>
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Driver List Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Drivers</CardTitle>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search drivers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
              {filteredDrivers.map(driver => (
                <DriverQuickCard
                  key={driver.id}
                  driver={driver}
                  isSelected={selectedDriver?.id === driver.id}
                  onClick={() => setSelectedDriver(driver)}
                />
              ))}
              {filteredDrivers.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <User className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No drivers found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content - Driver Details */}
        <div className="lg:col-span-3">
          {selectedDriver ? (
            <DriverCardLarge driver={selectedDriver} />
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Mic className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">Select a Driver</h3>
                <p className="text-sm text-slate-500">
                  Choose a driver from the list to view their information and statistics
                </p>
              </CardContent>
            </Card>
          )}

          {/* Keyboard Shortcut Hint */}
          <div className="mt-4 flex items-center justify-center gap-4 text-sm text-slate-400">
            <span className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-slate-100 rounded border border-slate-200 text-xs font-mono">←</kbd>
              Previous Driver
            </span>
            <span className="flex items-center gap-2">
              <kbd className="px-2 py-1 bg-slate-100 rounded border border-slate-200 text-xs font-mono">→</kbd>
              Next Driver
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
